/**
 * components/DataTable/TableRow
 * One body row. Split out from DataTable so the hover state lives on the row
 * that owns it — a single hovered index on the table would re-render every
 * row on every pointer move across a 200-row register.
 *
 * The rule the design is strict about: ROWS OPEN THINGS, BUTTONS ACT ON THINGS.
 * A row with no `onPress` gets no hover, no pointer cursor and no chevron — a
 * decided approval must not look clickable, because clicking it does nothing.
 */
import { memo, useState, type ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { defaultCursor, pointerCursor } from '../../theme/webStyle';
import { isWeb } from '../../lib/responsive';
import Icon from '../Icon';

export interface TableRowProps {
  cells: ReactNode[];
  widths: number[];
  aligns: ('left' | 'right' | 'center')[];
  onPress?: () => void;
  /** Show the `›` affordance. Only meaningful with `onPress`. */
  chevron?: boolean;
  /** Vertical row padding: the design uses 11, 12 or 13 depending on the table. */
  paddingY: number;
  /** Horizontal gutter: 15 on the finance tables, 16 on the approver tables. */
  gutter: number;
  /** Gap between columns: 12 or 16, per table. */
  columnGap: number;
  /** Hairline beneath. Off for the last row. */
  divided?: boolean;
  testID?: string;
}

function TableRowBase({
  cells,
  widths,
  aligns,
  onPress,
  chevron = false,
  paddingY,
  gutter,
  columnGap,
  divided = true,
  testID,
}: TableRowProps) {
  const [hovered, setHovered] = useState(false);
  const interactive = !!onPress;
  const active = hovered && isWeb && interactive;

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={interactive ? () => setHovered(true) : undefined}
      onHoverOut={interactive ? () => setHovered(false) : undefined}
      // Without a handler this must not announce itself as a button.
      accessibilityRole={interactive ? 'button' : undefined}
      disabled={!interactive}
      testID={testID}
      style={[
        styles.row,
        interactive ? pointerCursor : defaultCursor,
        {
          paddingVertical: paddingY,
          paddingHorizontal: gutter,
          gap: columnGap,
          backgroundColor: active ? neutral.surface2 : neutral.surface,
          borderBottomWidth: divided ? 1 : 0,
        },
      ]}
    >
      {cells.map((cell, index) => (
        <View
          key={index}
          style={{
            width: widths[index] ?? 0,
            alignItems:
              aligns[index] === 'right'
                ? 'flex-end'
                : aligns[index] === 'center'
                  ? 'center'
                  : 'flex-start',
            // The `minmax(0, …)` half of the contract: a cell may never be
            // widened by its content, or the body stops matching the header.
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {cell}
        </View>
      ))}
      {chevron && interactive && (
        <View style={styles.chevron}>
          <Icon name="chevronRight" size={13} color={neutral.grey300} strokeWidth={1.4} />
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    // Body rows are divided by the LIGHTER rule; only the header carries
    // the #E4E4E4 line.
    borderBottomColor: neutral.ruleLight,
  },
  chevron: { width: 18, alignItems: 'flex-end', flexShrink: 0 },
});

export const TableRow = memo(TableRowBase);
export default TableRow;
